import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import Editor from '@components/editor/Editor';

import { SelectedFileViewComponent } from '@features/task/components/TaskManageComponents/SelectedFileViewComponent';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import ApiClient from '@lib/apiClient';

import {
	addImageSuffix,
	getDefaultMention,
	getMinsCountFromNow,
	getTimeCount,
	onGetLocalFromUtc,
	parseHtml,
	printApiError
} from '@utils/Util';

const POST_TAG = {
	PRODUCT_QUOTE_COMMENT: 'Costing',
	PRODUCT_SAMPLE_COMMENT: 'Sample',
	PRODUCT_DEVELOPMENT_COMMENT: 'Design'
};

const SampleReply = ({
	postData,
	orderMemberList = [],
	commentSubmitUrl,
	commentSubmitparam,
	commentFrom,
	topic,
	userInfo,
	commentType,
	isAllCommentsOn,
	selectedPostTag
}) => {
	const [state, setState] = useState({
		post: postData,
		showSingleReply: true,
		enableReplyOption: false,
		comment: '',
		selectedFiles: [],
		orderMemberList: orderMemberList
	});
	const [editorContent, setEditorContent] = useState(null);
	const [mentionIds, setMentionIds] = useState([]);
	const [defaultMention, setDefaultMention] = useState(null);
	const dispatch = useDispatch();

	const recipients = {
		BUYER: <span className="buyer-sign px-1">Ⓑ</span>,
		NITEX: <span className="nitex-sign px-1">Ⓝ</span>
	};

	useEffect(() => {
		setState((prev) => ({
			...prev,
			orderMemberList: orderMemberList
		}));
		mentionedUserInfoSet();
	}, [orderMemberList]);

	const mentionedUserInfoSet = () => {
		const { postedBy } = postData;
		if (Object.keys(postedBy).length > 0) {
			setDefaultMention(getDefaultMention(postedBy.name, postedBy.id));
		}
	};

	const onMultipleFileSelect = async (e, docType) => {
		let { selectedFiles } = state;
		let files = Array.from(e.target.files);
		files.map((item) => {
			let data = {
				name: item.name,
				docMimeType: item.type,
				documentType: docType,
				print: false,
				base64Str: ''
			};
			let reader = new FileReader();
			reader.readAsDataURL(item);
			reader.onload = () => {
				data.base64Str = reader.result;
				selectedFiles.push(data);
				setState((prev) => ({ ...prev, selectedFiles }));
			};
		});
	};

	const removeFile = (index) => {
		let { selectedFiles } = state;
		setState((prev) => ({
			...prev,
			selectedFiles: selectedFiles.filter((file, i) => i !== index)
		}));
	};

	const filterComments = (comments) => {
		let { showSingleReply } = state;
		if (showSingleReply && comments.length) {
			return [comments[0]];
		}
		return comments;
	};

	const submitComment = async () => {
		const { selectedFiles, post } = state;
		dispatch(openLoader());
		const body = {
			documentDTOList: selectedFiles,
			postId: post.id,
			postType: 'COMMENT',
			text: editorContent,
			taggedUserIdList: mentionIds
		};
		if (
			['PRODUCT_SAMPLE_COMMENT', 'PRODUCT_QUOTE_COMMENT', 'PRODUCT_DEVELOPMENT_COMMENT'].includes(topic)
		) {
			body.postTag = topic;
		}
		await ApiClient.POST(commentSubmitUrl || 'addNewConversationComment', body, commentSubmitparam || '')
			.then(({ data }) => {
				dispatch(closeLoader());
				if (data.success) {
					if (commentFrom === 'ORDER') {
						post.commentList.push(data.payload.postResponse);
					} else {
						post.commentList.push(data.payload);
					}
					setState((prev) => ({
						...prev,
						comment: '',
						enableReplyOption: false,
						selectedFiles: [],
						post
					}));
				}
			})
			.catch((error) => {
				dispatch(closeLoader());
				printApiError(error);
			});
	};

	const renderPostedTime = (post) => {
		return (
			<div className="time">
				{getDateAndTime(post)}
				{post?.recipients?.map((item) => recipients[item.recipientType])}
				{post?.isSeen === false && <span className="blue-dot ml-1" />}
			</div>
		);
	};

	const getDateAndTime = (post) => {
		let prev = onGetLocalFromUtc(`${post?.postDate} ${post?.postTime}`);
		const inMins = getMinsCountFromNow(prev, 'YYYY-MM-DD HH:mm:ss');
		return getTimeCount(inMins, prev).toUpperCase();
	};

	const renderPostTitleExt = (post) => {
		if (post.postType === 'TASK_APPROVE' || post.postType === 'TASK_COMPLETE') {
			return (
				<>
					<span className="mx-2 action-name" style={{ color: '#00C334' }}>
						{post?.completedAction}
					</span>
					{post?.completedActedUpon ? post.completedActedUpon : post?.stepMaterial?.materialName}
				</>
			);
		} else if (post.postType === 'TASK_PRODUCTION_QUANTITY_UPDATE') {
			return (
				<>
					<span className="mx-2 action-name" style={{ color: '#6A7181' }}>
						updated production quantity on
					</span>
					{post.deliverableName}
				</>
			);
		} else if (post.postType === 'TASK_REVISION') {
			return (
				<>
					<span className="mx-2 action-name" style={{ color: '#F82B60' }}>
						requested for revision
					</span>
					<span style={{ color: 'rgba(0,0,0,.36)', marginRight: '4px' }}> on </span> {post.stepName}
				</>
			);
		} else {
			return null;
		}
	};

	let { post, showSingleReply, enableReplyOption, selectedFiles } = state;
	let postedBy = post.postedBy ? post.postedBy : {};

	let ACTUAL_COMMENT_STRING =
		editorContent === null ? '' : editorContent.toString().replace('<p></p>', '').trim();
	return (
		<>
			<div className="conversation">
				{commentType === 'SYSTEM_GENERATED' ? (
					<>
						<div className="conv-header">
							<img src={`${process.env.PUBLIC_URL}/icons/bell.svg`} />
							{renderPostedTime(post)}
						</div>
						<div className="task-status">{renderPostTitleExt(post)}</div>
					</>
				) : (
					<>
						<div className="conv-header">
							{postedBy.imageUrl ? (
								<img src={postedBy.imageUrl} alt="" />
							) : (
								<img src={`${process.env.PUBLIC_URL}/images/pro_pic_default.svg`} />
							)}
							<div className="name" />
							{renderPostedTime(post)}
						</div>
						<div className="task-status">{renderPostTitleExt(post)}</div>
					</>
				)}

				<div className="messages">
					<div
						className={`${commentType === 'SYSTEM_GENERATED' ? 'auto-generated-comment' : ''}`}
						dangerouslySetInnerHTML={{ __html: parseHtml(post.text) }}
					></div>
					{post.docList && post.docList.length ? (
						<div className="files-n-photos custom-scrollbar open">
							{post.docList.map((file, i) => {
								return (
									<SelectedFileViewComponent
										showRemoveOption={false}
										file={file}
										key={i}
										index={i}
									/>
								);
							})}
						</div>
					) : (
						<></>
					)}
					{!enableReplyOption && (
						<div className="reply-container">
							<button
								className="reply"
								onClick={() => setState((prev) => ({ ...prev, enableReplyOption: true }))}
							>
								Reply
							</button>
							{commentFrom === 'OTHER' && !selectedPostTag && (
								<span className={`ml-1 status ${POST_TAG[post?.postTag]}`}>
									{POST_TAG[post?.postTag]}
								</span>
							)}
							{isAllCommentsOn && (
								<span className="task-tag material ml-3">{post?.deliverableName}</span>
							)}
						</div>
					)}
				</div>
			</div>

			<div className="conversation conv-reply">
				{filterComments(post.commentList).map((comment) => {
					let commentPostedBy = comment?.postedBy ? comment.postedBy : {};
					return (
						<>
							<div className="conv-header">
								{commentPostedBy.imageUrl ? (
									<img src={commentPostedBy.imageUrl} alt="" />
								) : (
									<img
										src={`${process.env.PUBLIC_URL}/images/pro_pic_default.svg`}
										alt=""
									/>
								)}
								<div className="name">
									{commentPostedBy.name}{' '}
									{commentPostedBy.designation && (
										<span className="tag">{commentPostedBy.designation}</span>
									)}
								</div>
								{renderPostedTime(comment)}
							</div>
							<div className="messages">
								<p dangerouslySetInnerHTML={{ __html: parseHtml(comment.text) }} />
								{showSingleReply && post.commentList.length > 1 && (
									<div className="reply-container">
										<button
											className="view-reply"
											onClick={() =>
												setState((prev) => ({ ...prev, showSingleReply: false }))
											}
										>
											View {post.commentList.length - 1} replies
										</button>
									</div>
								)}
								{comment.docList && comment.docList.length ? (
									<div className="files-n-photos custom-scrollbar open">
										{comment.docList.map((file, i) => {
											return (
												<SelectedFileViewComponent
													showRemoveOption={false}
													file={file}
													key={i}
													index={i}
													remove={removeFile}
												/>
											);
										})}
									</div>
								) : (
									<></>
								)}
							</div>
						</>
					);
				})}
				{enableReplyOption ? (
					<div className="reply-option">
						<div className="user-photo">
							{userInfo && userInfo.profilePicDocument ? (
								<img src={addImageSuffix(userInfo.profilePicDocument.docUrl, '_xicon')} />
							) : (
								<img src={`${process.env.PUBLIC_URL}/images/pro_pic_default.svg`} />
							)}
						</div>
						<Editor
							defaultMention={defaultMention}
							setEditorContent={setEditorContent}
							setMentionIds={setMentionIds}
							memberList={orderMemberList?.map((user) => {
								return {
									memberId: user.id,
									value: user.name
								};
							})}
						/>
						<div
							className={`files-n-photos custom-scrollbar ${
								selectedFiles.length ? `open` : ``
							}`}
						>
							{selectedFiles.map((file, i) => {
								return (
									<SelectedFileViewComponent
										showRemoveOption={true}
										file={file}
										key={i}
										index={i}
										remove={removeFile}
									/>
								);
							})}
						</div>

						<div className="post-actions">
							<div className="attachment cursor-pointer">
								<label htmlFor="input-file-reply">
									<svg
										width="24"
										height="24"
										viewBox="0 0 24 24"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M12.5 22C9.46 22 7 19.54 7 16.5L7 6C7 3.79 8.79 2 11 2C13.21 2 15 3.79 15 6L15 14.5C15 15.88 13.88 17 12.5 17C11.12 17 10 15.88 10 14.5L10 7L12 7L12 14.59C12 15.14 13 15.14 13 14.59L13 6C13 4.9 12.1 4 11 4C9.9 4 9 4.9 9 6L9 16.5C9 18.43 10.57 20 12.5 20C14.43 20 16 18.43 16 16.5L16 7L18 7L18 16.5C18 19.54 15.54 22 12.5 22Z"
											fill="#595959"
										/>
									</svg>
								</label>
								<input
									id="input-file-reply"
									type="file"
									name="selectedFiles"
									onChange={(e) => onMultipleFileSelect(e, 'ACCESSORIES_DESIGN')}
									multiple
								/>
							</div>
							<div className="save-note">
								<button
									className={`text-white ${!ACTUAL_COMMENT_STRING ? `brand-bg-color` : ``}`}
									onClick={submitComment}
									disabled={!ACTUAL_COMMENT_STRING}
								>
									Reply
								</button>
								<div
									className="close-note cursor-pointer"
									onClick={() =>
										setState((prev) => ({
											...prev,
											comment: '',
											selectedFiles: [],
											enableReplyOption: false
										}))
									}
								>
									<svg
										width="14"
										height="15"
										viewBox="0 0 14 15"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M14 1.91L12.59 0.5L7 6.09L1.41 0.5L0 1.91L5.59 7.5L0 13.09L1.41 14.5L7 8.91L12.59 14.5L14 13.09L8.41 7.5L14 1.91Z"
											fill="#222222"
										/>
									</svg>
								</div>
							</div>
						</div>
					</div>
				) : (
					<></>
				)}
			</div>
		</>
	);
};

export default SampleReply;
